import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { dashboardAPI, seedAPI, signingRequestsAPI } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  FileText,
  FolderKanban,
  Euro,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  ArrowRight,
  Database,
  Eye,
  PenLine
} from "lucide-react";

const STATUS_LABELS = {
  neu: "Neu",
  angebot_erstellt: "Angebot erstellt",
  angebot_angenommen: "Angenommen",
  angebot_abgelehnt: "Abgelehnt",
  planung: "In Planung",
  montage_geplant: "Montage geplant",
  in_montage: "In Montage",
  abgeschlossen: "Abgeschlossen",
  bezahlt: "Bezahlt"
};

function formatCurrency(amount) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
}

function StatsCard({ title, value, icon: Icon, subtitle, accent }) {
  return (
    <Card className={`card-hover ${accent ? "bg-accent/5 border-accent/20" : ""}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className={`text-sm font-medium ${accent ? "text-accent" : "text-muted-foreground"}`}>
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 ${accent ? "text-accent" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold font-['Manrope'] ${accent ? "text-accent" : ""}`}>
          {value}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

function OfferBadges({ accepted, pending, rejected }) {
  return (
    <div className="flex gap-2 mt-2">
      <Badge variant="outline" className="status-angebot_angenommen">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {accepted}
      </Badge>
      <Badge variant="outline" className="status-angebot_erstellt">
        <Clock className="h-3 w-3 mr-1" />
        {pending}
      </Badge>
      <Badge variant="outline" className="status-angebot_abgelehnt">
        <XCircle className="h-3 w-3 mr-1" />
        {rejected}
      </Badge>
    </div>
  );
}

// Signing Stats Card Component
function SigningStatsCard({ stats, loading }) {
  if (loading) {
    return (
      <Card className="col-span-full lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            Signaturen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const scopeLabel = stats.scope === "personal" ? "Meine Angebote" : 
                     stats.scope === "company" ? "Firma" : "Alle Firmen";

  return (
    <Card className="col-span-full lg:col-span-2" data-testid="signing-stats-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            Signaturen
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {scopeLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          {/* Warten auf Unterschrift */}
          <Link 
            to="/signaturen?status=pending" 
            className="group p-4 rounded-lg bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 transition-colors"
            data-testid="signing-pending-link"
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-yellow-600" aria-hidden="true" />
              <span className="text-xs font-medium text-yellow-700">Warten auf Unterschrift</span>
            </div>
            <div className="text-2xl font-bold text-yellow-800">
              {stats.pending_count}
            </div>
            <div className="text-xs text-yellow-600 mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Anzeigen <ArrowRight className="h-3 w-3" />
            </div>
          </Link>

          {/* Heute geöffnet */}
          <Link 
            to="/signaturen?status=opened" 
            className="group p-4 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
            data-testid="signing-opened-link"
          >
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-blue-600" aria-hidden="true" />
              <span className="text-xs font-medium text-blue-700">Heute geöffnet</span>
            </div>
            <div className="text-2xl font-bold text-blue-800">
              {stats.opened_today_count}
            </div>
            <div className="text-xs text-blue-600 mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Anzeigen <ArrowRight className="h-3 w-3" />
            </div>
          </Link>

          {/* Diese Woche unterschrieben */}
          <Link 
            to="/signaturen?status=completed" 
            className="group p-4 rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 transition-colors"
            data-testid="signing-completed-link"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
              <span className="text-xs font-medium text-green-700">Diese Woche unterschrieben</span>
            </div>
            <div className="text-2xl font-bold text-green-800">
              {stats.completed_week_count}
            </div>
            <div className="text-xs text-green-600 mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Anzeigen <ArrowRight className="h-3 w-3" />
            </div>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentProjects({ projects }) {
  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FolderKanban className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>Noch keine Projekte vorhanden</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/kunden">Ersten Kunden anlegen</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {projects.map((project) => (
        <div
          key={project.id}
          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
        >
          <div>
            <p className="font-medium">{project.customer_name}</p>
            <p className="text-sm text-muted-foreground font-mono">
              {project.project_number}
            </p>
          </div>
          <Badge className={`status-${project.status}`}>
            {STATUS_LABELS[project.status] || project.status}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function PipelineOverview({ projectsByStatus, totalProjects }) {
  const entries = Object.entries(STATUS_LABELS);
  
  return (
    <div className="space-y-3">
      {entries.map(([key, label]) => {
        const count = projectsByStatus ? projectsByStatus[key] || 0 : 0;
        const total = totalProjects || 1;
        const percentage = (count / total) * 100;
        
        let barColor = "bg-accent";
        if (key.includes("angenommen") || key === "abgeschlossen") {
          barColor = "bg-green-500";
        } else if (key.includes("abgelehnt")) {
          barColor = "bg-red-500";
        } else if (key === "bezahlt") {
          barColor = "bg-teal-500";
        }
        
        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span>{label}</span>
              <span className="font-medium">{count}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [signingStats, setSigningStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingLoading, setSigningLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const { user } = useAuth();

  const loadStats = useCallback(async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
      if (error.response && error.response.status === 400) {
        setStats({
          total_customers: 0,
          total_projects: 0,
          total_offers: 0,
          offers_accepted: 0,
          offers_pending: 0,
          offers_rejected: 0,
          total_revenue: 0,
          projects_by_status: {},
          recent_projects: []
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSigningStats = useCallback(async () => {
    try {
      const response = await signingRequestsAPI.getStats();
      setSigningStats(response.data);
    } catch (error) {
      console.error("Failed to load signing stats:", error);
      // Silent fail - signing stats are optional
    } finally {
      setSigningLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadSigningStats();
  }, [loadStats, loadSigningStats]);

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      await seedAPI.seed();
      toast.success("Datenbank erfolgreich initialisiert");
      loadStats();
    } catch (error) {
      console.error("Failed to seed:", error);
      const message = error.response && error.response.data && error.response.data.detail 
        ? error.response.data.detail 
        : "Fehler beim Initialisieren";
      toast.error(message);
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const userName = user ? user.first_name : "";
  const userRole = user ? user.role : "";
  const isVertriebler = stats?.is_vertriebler || userRole === "vertriebler";
  const totalCustomers = stats ? stats.total_customers : 0;
  const totalProjects = stats ? stats.total_projects : 0;
  const totalOffers = stats ? stats.total_offers : 0;
  const offersAccepted = stats ? stats.offers_accepted : 0;
  const offersPending = stats ? stats.offers_pending : 0;
  const offersRejected = stats ? stats.offers_rejected : 0;
  const totalRevenue = stats ? stats.total_revenue : 0;
  const monthlyCommission = stats?.monthly_commission || 0;
  const pendingCommission = stats?.pending_commission || 0;
  const projectsByStatus = stats ? stats.projects_by_status : {};
  const recentProjects = stats ? stats.recent_projects : [];

  return (
    <div className="space-y-8 animate-slide-in" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Dashboard</h1>
          <p className="text-muted-foreground">
            Willkommen zurück, {userName}!
          </p>
        </div>
        <div className="flex gap-3">
          {userRole === "superadmin" && (
            <Button
              variant="outline"
              onClick={handleSeedDatabase}
              disabled={seeding}
              data-testid="seed-btn"
            >
              <Database className="h-4 w-4 mr-2" />
              {seeding ? "Initialisiere..." : "Datenbank initialisieren"}
            </Button>
          )}
          <Button asChild data-testid="new-customer-btn">
            <Link to="/kunden">
              <Plus className="h-4 w-4 mr-2" />
              Neuer Kunde
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Kunden"
          value={totalCustomers}
          icon={Users}
          subtitle="Registrierte Kunden"
        />
        <StatsCard
          title="Projekte"
          value={totalProjects}
          icon={FolderKanban}
          subtitle="Aktive Projekte"
        />
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Angebote
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-['Manrope']">
              {totalOffers}
            </div>
            <OfferBadges 
              accepted={offersAccepted} 
              pending={offersPending} 
              rejected={offersRejected} 
            />
          </CardContent>
        </Card>
        
        {/* Provision für Vertriebler, Umsatz für andere */}
        {isVertriebler ? (
          <Card className="card-hover bg-green-500/5 border-green-500/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-green-600">
                Provision
              </CardTitle>
              <Euro className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-['Manrope'] text-green-600">
                {formatCurrency(monthlyCommission)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                voraussichtlich diesen Monat
              </p>
              {pendingCommission > 0 && (
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  + {formatCurrency(pendingCommission)} ausstehend
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <StatsCard
            title="Umsatz"
            value={formatCurrency(totalRevenue)}
            icon={Euro}
            subtitle="Aus angenommenen Angeboten"
            accent
          />
        )}
      </div>

      {/* Signing Stats Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SigningStatsCard stats={signingStats} loading={signingLoading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-['Manrope']">Aktuelle Projekte</CardTitle>
                <CardDescription>Die neuesten Projekte auf einen Blick</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/pipeline">
                  Alle anzeigen
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <RecentProjects projects={recentProjects} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-['Manrope']">Pipeline Übersicht</CardTitle>
                <CardDescription>Projekte nach Status</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/pipeline">
                  Zur Pipeline
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <PipelineOverview 
              projectsByStatus={projectsByStatus} 
              totalProjects={totalProjects} 
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
